/* iter-romance28 — Read-only canonical content-tag list.
 * Consumed by the storefront chip renderers (BookCard, BookDetailModal,
 * GenrePage) so canonical tags carry a filled ShieldCheck badge
 * beside the label. Cached in-memory + localStorage (7-day TTL) so we
 * hit the API at most once per week per browser. */
import { useEffect, useState } from 'react';
import { bookstoreApi } from '@/lib/bookstoreApi';

const CACHE_KEY = 'bs-canonical-tags';
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000;  // 7 days

let memo = null;  // module-level cache so many chip renderers share one fetch

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { tags, at } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL) return null;
    return new Set(tags);
  } catch { return null; }
}

function writeCache(tagsSet) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      tags: [...tagsSet], at: Date.now(),
    }));
  } catch { /* SSR / quota */ }
}

export default function useCanonicalTags() {
  const [tags, setTags] = useState(() => memo || readCache() || new Set());

  useEffect(() => {
    if (memo) return;                       // one fetch per session
    if (readCache()) { memo = tags; return; } // warm cache, no fetch
    bookstoreApi.canonicalTags?.().then((r) => {
      const set = new Set((r?.tags || []).map((t) => String(t).toLowerCase()));
      memo = set;
      writeCache(set);
      setTags(set);
    }).catch(() => { memo = new Set(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isCanonical = (t) => tags.has(String(t || '').toLowerCase());
  return { tags, isCanonical };
}
